import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";

import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import styles from "./EmailCaptureView.module.css";
import { useAppContext } from "../../context/AppContext";
import Header from "../Header";
import Button from "../Button";
import PoweredBy from "../PoweredBy";
import Heading from "../Heading";
import ItemsModalView from "./ItemsModalView";

const KioskSignUpView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state, dispatch } = useAppContext();
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    dateOfBirth: ""
  });
  
  const [currentField, setCurrentField] = useState<string | null>(null);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [layoutName, setLayoutName] = useState("default");
  const [showItemsModal, setShowItemsModal] = useState(false);
  
  const keyboardRef = useRef<any>(null);
  const keyboardContainerRef = useRef<HTMLDivElement>(null);
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const lastKeyboardInput = useRef<string>('');

  // Get email from URL params if passed from QR code
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setFormData(prev => ({
        ...prev,
        email: emailParam
      }));
    }
  }, [searchParams]);

  // Debug: Log current state values
  useEffect(() => {
    console.log('Current state:', { formData, currentField, showKeyboard });
  }, [formData, currentField, showKeyboard]);

  const handleFieldFocus = (field: string) => {
    console.log('Field focused:', field);
    setCurrentField(field);
    setShowKeyboard(true);
    
    // Small delay to ensure keyboard is rendered before setting input
    setTimeout(() => {
      if (keyboardRef.current) {
        const currentValue = formData[field as keyof typeof formData] as string;
        console.log('Setting keyboard input to:', currentValue);
        // Set the current field value directly
        keyboardRef.current.setInput(currentValue);
        lastKeyboardInput.current = currentValue;
      }
    }, 150);
  };

  const onKeyboardChange = (input: string) => {
    console.log('Keyboard input changed:', { input, currentField, lastInput: lastKeyboardInput.current });
    
    if (currentField) {
      // Only process if this is a real user input, not a programmatic set
      if (input === lastKeyboardInput.current) {
        console.log('Input matches last set value, skipping update');
        return;
      }
      
      let formattedInput = input;
      
      // Format date of birth as MM/DD/YYYY when typing
      if (currentField === 'dateOfBirth') {
        const digitsOnly = input.replace(/\D/g, ''); // Remove non-digits
        if (digitsOnly.length >= 2) {
          formattedInput = digitsOnly.substring(0, 2) + '/' + digitsOnly.substring(2);
        }
        if (digitsOnly.length >= 4) {
          formattedInput = digitsOnly.substring(0, 2) + '/' + digitsOnly.substring(2, 4) + '/' + digitsOnly.substring(4, 8);
        }
        if (digitsOnly.length > 8) {
          formattedInput = digitsOnly.substring(0, 2) + '/' + digitsOnly.substring(2, 4) + '/' + digitsOnly.substring(4, 8);
        }
      }
      
      console.log(`Updating ${currentField} to:`, formattedInput);
      setFormData(prev => ({
        ...prev,
        [currentField]: formattedInput
      }));
    }
  };

  const onKeyPress = (button: string) => {
    console.log('Key pressed:', button);
    
    if (button === "{shift}" || button === "{lock}") {
      setLayoutName(layoutName === "default" ? "shift" : "default");
    }
    
    if (button === "{bksp}") {
      // Handle backspace
      if (currentField) {
        const currentValue = formData[currentField as keyof typeof formData] as string;
        let newValue = currentValue.slice(0, -1);
        
        // For date field, reformat after backspace
        if (currentField === 'dateOfBirth') {
          const digitsOnly = newValue.replace(/\D/g, '');
          newValue = '';
          if (digitsOnly.length >= 2) {
            newValue = digitsOnly.substring(0, 2) + '/' + digitsOnly.substring(2);
          } else {
            newValue = digitsOnly;
          }
          if (digitsOnly.length >= 4) {
            newValue = digitsOnly.substring(0, 2) + '/' + digitsOnly.substring(2, 4) + '/' + digitsOnly.substring(4, 8);
          }
        }
        
        console.log('Backspace on field:', { field: currentField, from: currentValue, to: newValue });
        
        setFormData(prev => ({
          ...prev,
          [currentField]: newValue
        }));
        
        // Update keyboard display immediately
        setTimeout(() => {
          if (keyboardRef.current) {
            keyboardRef.current.setInput(newValue);
          }
        }, 10);
      }
    }
    
    if (button === "{enter}") {
      handleSignUp();
    }
  };

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Node;
    const isOutsideKeyboard = keyboardContainerRef.current && !keyboardContainerRef.current.contains(target);
    
    // Check if click is outside all input fields
    const isOutsideAllInputs = Object.values(inputRefs.current).every(ref => 
      ref && !ref.contains(target)
    );
    
    console.log('Click outside check:', { isOutsideKeyboard, isOutsideAllInputs, showKeyboard });
    
    if (showKeyboard && isOutsideKeyboard && isOutsideAllInputs) {
      console.log('Hiding keyboard due to outside click');
      setShowKeyboard(false);
      setCurrentField(null);
    }
  }, [showKeyboard]);

  useEffect(() => {
    if (showKeyboard) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showKeyboard, handleClickOutside]);

  // Sync keyboard display with current field value when values change
  useEffect(() => {
    if (showKeyboard && keyboardRef.current && currentField) {
      const currentValue = formData[currentField as keyof typeof formData] as string;
      console.log('Syncing keyboard with field value:', { currentField, currentValue });
      keyboardRef.current.setInput(currentValue);
      lastKeyboardInput.current = currentValue;
    }
  }, [formData, showKeyboard, currentField]);

  const handleSignUp = () => {
    const { firstName, lastName, email, password } = formData;
    
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !password.trim()) {
      return;
    }
    
    // Mock sign up logic
    console.log("Kiosk sign up attempt:", formData);
    
    // Update app context with user info
    dispatch({ type: 'SET_EMAIL', payload: email });
    dispatch({ type: 'SET_MEMBER', payload: true });
    dispatch({ type: 'SET_GUEST', payload: false });
    dispatch({ type: 'SET_IS_SIGNUP', payload: true });
    
    // Navigate to reward method selection
    navigate("/reward-method-selection");
  };

  const handleSocialLogin = (provider: string) => {
    console.log(`${provider} signup attempt from kiosk`);
    
    // Update app context with user info
    dispatch({ type: 'SET_EMAIL', payload: formData.email || `user@${provider.toLowerCase()}.com` });
    dispatch({ type: 'SET_MEMBER', payload: true });
    dispatch({ type: 'SET_GUEST', payload: false });
    dispatch({ type: 'SET_IS_SIGNUP', payload: true });
    
    navigate("/reward-method-selection");
  };

  const handleBack = () => {
    navigate("/membership-selection");
  };

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };



  return (
    <div className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden">
      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col gap-24">
        {/* Header Section */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={state.itemsCount > 0}
              itemsNumber={state.itemsCount.toString()}
              
              
              
              
              
              onItemsClick={handleItemsClick}
            />
          </div>
        </div>

        {/* Main Content Area - with 3-row grid */}
        <motion.div 
          className="flex-1 grid grid-rows-3 gap-0 mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Row 1 - Heading (hidden when keyboard is active) */}
          <AnimatePresence>
            {!showKeyboard && (
              <motion.div 
                className="flex items-center justify-center"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{
                  duration: 0.3,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                <div className="text-center w-full max-w-[920px]">
                  <Heading 
                    title="Create Account" 
                    subtitle="Fill in your details to become a member" 
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Form Section - spans rows 2-3 normally, rows 1-3 when keyboard is active */}
          <motion.div 
            className={showKeyboard ? "row-span-3 flex items-center justify-center" : "row-span-2 flex items-center justify-center"}
            layout
            transition={{
              duration: 0.3,
              ease: [0.25, 0.1, 0.25, 1],
            }}
          >
            <motion.div
              className="w-full max-w-[700px] flex flex-col justify-center space-y-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: showKeyboard ? 0 : 0.2,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-6">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    duration: 0.4,
                    delay: 0.3,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                >
                  <input
                    ref={(el) => { inputRefs.current['firstName'] = el; }}
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => {
                      console.log('First name changed via field:', e.target.value);
                      setFormData(prev => ({ ...prev, firstName: e.target.value }));
                    }}
                    onFocus={() => handleFieldFocus('firstName')}
                    autoComplete="off"
                    className={`w-full h-[88px] px-6 text-[32px] leading-[58px] font-light tracking-[-0.8px] text-center border-0 border-b-2 bg-transparent outline-none transition-colors duration-200 ${
                      currentField === 'firstName' 
                        ? "bg-brand-background border-brand-primary text-brand-text-primary placeholder-brand-text-muted" 
                        : "bg-brand-background border-brand-text-primary text-brand-text-primary placeholder-brand-text-muted focus:border-brand-primary"
                    }`}
                    placeholder="first name"
                    style={{
                      fontFamily: "var(--brand-font-primary), sans-serif",
                      WebkitBoxShadow: '0 0 0 1000px transparent inset',
                      WebkitTextFillColor: 'inherit',
                      backgroundColor: 'transparent',
                    }}
                    required
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    duration: 0.4,
                    delay: 0.35,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                >
                  <input
                    ref={(el) => { inputRefs.current['lastName'] = el; }}
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => {
                      console.log('Last name changed via field:', e.target.value);
                      setFormData(prev => ({ ...prev, lastName: e.target.value }));
                    }}
                    onFocus={() => handleFieldFocus('lastName')}
                    autoComplete="off"
                    className={`w-full h-[88px] px-6 text-[32px] leading-[58px] font-light tracking-[-0.8px] text-center border-0 border-b-2 bg-transparent outline-none transition-colors duration-200 ${
                      currentField === 'lastName' 
                        ? "bg-brand-background border-brand-primary text-brand-text-primary placeholder-brand-text-muted" 
                        : "bg-brand-background border-brand-text-primary text-brand-text-primary placeholder-brand-text-muted focus:border-brand-primary"
                    }`}
                    placeholder="last name"
                    style={{
                      fontFamily: "var(--brand-font-primary), sans-serif",
                      WebkitBoxShadow: '0 0 0 1000px transparent inset',
                      WebkitTextFillColor: 'inherit',
                      backgroundColor: 'transparent',
                    }}
                    required
                  />
                </motion.div>
              </div>

              {/* Email Field */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.4,
                  delay: 0.4,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                <input
                  ref={(el) => { inputRefs.current['email'] = el; }}
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    console.log('Email changed via field:', e.target.value);
                    setFormData(prev => ({ ...prev, email: e.target.value }));
                  }}
                  onFocus={() => handleFieldFocus('email')}
                  autoComplete="off"
                  className={`w-full h-[88px] px-6 text-[32px] leading-[58px] font-light tracking-[-0.8px] text-center border-0 border-b-2 bg-transparent outline-none transition-colors duration-200 ${
                    currentField === 'email' 
                      ? "border-brand-primary text-brand-text-primary placeholder-brand-text-muted" 
                      : "border-brand-text-primary text-brand-text-primary placeholder-brand-text-muted focus:border-brand-primary"
                  }`}
                  placeholder="Email address"
                  style={{
                    fontFamily: "var(--brand-font-primary), sans-serif",
                    WebkitBoxShadow: '0 0 0 1000px transparent inset',
                    WebkitTextFillColor: 'inherit',
                    backgroundColor: 'transparent',
                  }}
                  required
                />
              </motion.div>

              {/* Password Field */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.4,
                  delay: 0.5,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                <input
                  ref={(el) => { inputRefs.current['password'] = el; }}
                  type="password"
                  value={formData.password}
                  onChange={(e) => {
                    console.log('Password changed via field:', e.target.value);
                    setFormData(prev => ({ ...prev, password: e.target.value }));
                  }}
                  onFocus={() => handleFieldFocus('password')}
                  autoComplete="off"
                  className={`w-full h-[88px] px-6 text-[32px] leading-[58px] font-light tracking-[-0.8px] text-center border-0 border-b-2 bg-transparent outline-none transition-colors duration-200 ${
                    currentField === 'password' 
                      ? "border-brand-primary text-brand-text-primary placeholder-brand-text-muted" 
                      : "border-brand-text-primary text-brand-text-primary placeholder-brand-text-muted focus:border-brand-primary"
                  }`}
                  placeholder="Password"
                  style={{
                    fontFamily: "var(--brand-font-primary), sans-serif",
                    WebkitBoxShadow: '0 0 0 1000px transparent inset',
                    WebkitTextFillColor: 'inherit',
                    backgroundColor: 'transparent',
                  }}
                  required
                />
              </motion.div>

              {/* Date of Birth Field */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.4,
                  delay: 0.55,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                                  <input
                    ref={(el) => { inputRefs.current['dateOfBirth'] = el; }}
                    type="text"
                    value={formData.dateOfBirth}
                    onChange={(e) => {
                      // Format input as MM/DD/YYYY
                      let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                      if (value.length >= 2) {
                        value = value.substring(0, 2) + '/' + value.substring(2);
                      }
                      if (value.length >= 5) {
                        value = value.substring(0, 5) + '/' + value.substring(5, 9);
                      }
                      console.log('Date of birth changed via field:', value);
                      setFormData(prev => ({ ...prev, dateOfBirth: value }));
                    }}
                    onFocus={() => handleFieldFocus('dateOfBirth')}
                    autoComplete="off"
                    maxLength={10}
                    className={`w-full h-[88px] px-6 text-[32px] leading-[58px] font-light tracking-[-0.8px] text-center border-0 border-b-2 bg-transparent outline-none transition-colors duration-200 ${
                      currentField === 'dateOfBirth' 
                        ? "bg-brand-background border-brand-primary text-brand-text-primary placeholder-brand-text-muted" 
                        : "bg-brand-background border-brand-text-primary text-brand-text-primary placeholder-brand-text-muted focus:border-brand-primary"
                    }`}
                    placeholder="MM/DD/YYYY"
                    style={{
                      fontFamily: "var(--brand-font-primary), sans-serif",
                      WebkitBoxShadow: '0 0 0 1000px transparent inset',
                      WebkitTextFillColor: 'inherit',
                      backgroundColor: 'transparent',
                    }}
                    required
                  />
              </motion.div>



              {/* Social Login Options - animated visibility when keyboard is active */}
              <AnimatePresence>
                {!showKeyboard && (
                  <motion.div 
                    className="pt-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{
                      duration: 0.3,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                  >
                    <p className="text-center font-brand-primary mb-6 text-[24px] leading-[32px]">Or sign up with</p>
                    
                    <div className="flex gap-8">
                      <Button variant="tertiary" onClick={() => handleSocialLogin('Facebook')}>
                        <div className="flex items-center gap-3">
                          <img src="/facebook-icon.png" alt="Facebook" className="w-6 h-6" />
                          <span>Facebook</span>
                        </div>
                      </Button>
                      <Button variant="tertiary" onClick={() => handleSocialLogin('Google')}>
                        <div className="flex items-center gap-3">
                          <img src="/google-icon.png" alt="Google" className="w-6 h-6" />
                          <span>Google</span>
                        </div>
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Bottom Section */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Back Button */}
          <div className="flex justify-center w-full max-w-[282px]">
            <Button variant="tertiary" onClick={handleBack}>
              Back
            </Button>
          </div>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>

      {/* Virtual Keyboard */}
      <AnimatePresence>
        {showKeyboard && currentField && (
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-50 bg-brand-background"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{
              duration: 0.2,
              ease: [0.25, 0.1, 0.25, 1],
            }}
            ref={keyboardContainerRef}
          >
            <div className={styles.keyboardContainer}>
              <Keyboard
                ref={keyboardRef}
                layoutName={layoutName}
                onChange={onKeyboardChange}
                onKeyPress={onKeyPress}
                theme="hg-theme-default hg-layout-default"
                layout={{
                  default: [
                    "1 2 3 4 5 6 7 8 9 0 {bksp}",
                    "q w e r t y u i o p",
                    "a s d f g h j k l",
                    "{shift} z x c v b n m {shift}",
                    "{space} @ . / {enter}"
                  ],
                  shift: [
                    "! @ # $ % ^ & * ( ) {bksp}",
                    "Q W E R T Y U I O P",
                    "A S D F G H J K L",
                    "{shift} Z X C V B N M {shift}",
                    "{space} @ . / {enter}"
                  ]
                }}
                display={{
                  '{bksp}': '⌫',
                  '{enter}': 'Create Account',
                  '{shift}': '⇧',
                  '{space}': 'Space',
                  '@': '@',
                  '.': '.',
                  '/': '/'
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Items Modal */}
      <ItemsModalView
        isOpen={showItemsModal}
        onClose={handleCloseItemsModal}
      />
    </div>
  );
};

export default KioskSignUpView;
